import { useMemo, useRef, useState } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { CreditBalance } from '../Credits/CreditBalance'
import { Button, Fab, IconButton, Pressable } from '../Common'
import { PulseReportListRow } from '../Ai/PulseReportListRow'
import { PulseReportReader } from '../Ai/PulseReportReader'
import { PulseReportRunSheet } from '../Ai/PulseReportRunSheet'
import { MemberActionCard, MemberBottomSheet, MemberEmptyState } from './controls/MemberControlsPrimitives'
import { MemberNexusHero } from './controls/MemberNexusPrimitives'
import { useMemberPulse } from '../../context/memberPulseContextCore'
import { useAccessRestriction } from '../../hooks/useAccessRestriction'
import { useNls } from '../../nls/nlsProvider'
import { trackPulseReportEvent } from '../../services/pulseReportTelemetry'
import type { WorkspacePulseSnapshot } from '../../services/aiAgentBridge'
import type { PulseReport, PulseReportKind } from '../../types/pulseReport'

import { pulseMetricStrip, type PulseMetricId } from './MemberAiPulseTab.helpers'

const statusCopy: Record<WorkspacePulseSnapshot['healthState'], string> = {
  HEALTHY: 'Healthy',
  WATCH: 'Watch',
  CRITICAL: 'Critical',
}

const eventTone = (severity: string) => {
  if (severity === 'CRITICAL') return 'danger'
  if (severity === 'WATCH' || severity === 'WARN') return 'warning'
  return 'info'
}

export const MemberAiPulseTab = ({
  workspaceId,
  walletCredits,
  onOpenBoardDashboard,
  onWalletClick,
}: {
  workspaceId: string
  uid: string
  walletCredits: number
  onOpenBoardDashboard?: () => void
  onWalletClick?: () => void
}) => {
  const {
    pulse,
    reports,
    events,
    error,
    reportsUnavailable,
    refreshPulse,
    setError,
    pinReport,
    deleteReport,
    runReport,
  } = useMemberPulse()
  const { restricted } = useAccessRestriction()
  const { t } = useNls()

  const [runSheetOpen, setRunSheetOpen] = useState(false)
  const [readerReport, setReaderReport] = useState<PulseReport | null>(null)
  const [runningKind, setRunningKind] = useState<PulseReportKind | null>(null)
  const [activeMetric, setActiveMetric] = useState<PulseMetricId | null>(null)
  const reportsRef = useRef<HTMLElement | null>(null)
  const metrics = useMemo(() => pulseMetricStrip(pulse), [pulse])

  const run = async (kind: PulseReportKind) => {
    const startedAt = Date.now()
    setRunningKind(kind)
    setError(null)
    trackPulseReportEvent({ name: 'pulse_report_started', props: { workspaceId, kind, source: 'button' } })
    try {
      const result = await runReport(kind)
      trackPulseReportEvent({
        name: 'pulse_report_completed',
        props: {
          reportId: result.reportId ?? '',
          kind,
          cached: result.cached === true,
          creditsCharged: result.creditsCharged ?? 0,
          durationMs: Date.now() - startedAt,
        },
      })
      setRunSheetOpen(false)
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Pulse report failed.'
      setError(message)
      trackPulseReportEvent({ name: 'pulse_report_failed', props: { kind, message } })
    } finally {
      setRunningKind(null)
    }
  }

  const liveFeed = events.length ? events.slice(0, 3) : pulse.activity.latest.slice(0, 3).map((entry) => ({
    id: entry.id,
    workspaceId,
    type: entry.action,
    severity: 'INFO',
    title: entry.action || 'Activity',
    summary: entry.summary,
    targetId: entry.targetId,
    targetType: 'activity',
    createdAt: null,
  }))

  const activeMetricCopy = activeMetric ? metrics.find((metric) => metric.id === activeMetric) : null

  if (restricted) {
    return (
      <div className="memberAiPulsePanel" data-ui-name="UI-MPAI-PLS">
        <div className="memberAiPulseRestricted">
          {t('NLSAiPulseRestricted')}
        </div>
      </div>
    )
  }

  return (
    <div className="memberAiPulsePanel" data-ui-name="UI-MPAI-PLS">
      <MemberNexusHero
        eyebrow="AI Magic"
        title="Pulse"
        rightSlot={(
          <div className="memberAiHeroActions">
            <IconButton variant="tertiary" size="md" onClick={refreshPulse} label="Refresh Pulse" icon={<RefreshRoundedIcon fontSize="inherit" />} />
            <Pressable
              className="memberNexusHero__chip memberTapRipple"
              onClick={() => reportsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              <span>Reports</span>
              <strong>{reports.length}</strong>
            </Pressable>
            <CreditBalance value={walletCredits} variant="bare" scope="workspace" label={null} className="memberAiWalletChip" onClick={onWalletClick} title="Workspace credits" />
          </div>
        )}
      />
      <div className="memberAiPulseScrollRegion" data-testid="member-ai-pulse-scroll-region">
        <MemberActionCard
        icon={<RadioButtonCheckedRoundedIcon fontSize="inherit" />}
        title={statusCopy[pulse.healthState]}
        sub={`${pulse.openRiskCount} open risks, ${pulse.failedAutomationCount} failed automations, ${pulse.stockShortageCount} stock pressure signals.`}
        status={`${pulse.riskScore}/100 risk score`}
        statusTone={pulse.healthState === 'CRITICAL' ? 'danger' : pulse.healthState === 'WATCH' ? 'warning' : 'success'}
        className={`memberActionCard--health memberActionCard--health-${pulse.healthState.toLowerCase()}`}
        />
        <div className="memberAiMetricStrip" aria-label="Pulse metrics">
        {metrics.map((metric) => (
          <Pressable key={metric.id} aria-pressed={activeMetric === metric.id} className="memberNexusHero__chip memberTapRipple" onClick={() => setActiveMetric(metric.id)}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </Pressable>
        ))}
        </div>
        <section className="memberAiLiveFeed">
        <header><strong>Live feed</strong></header>
        {liveFeed.length ? liveFeed.map((event) => (
          <MemberActionCard
            key={event.id}
            icon={<RadioButtonCheckedRoundedIcon fontSize="inherit" />}
            title={event.title}
            sub={event.summary}
            status={event.severity}
            statusTone={eventTone(event.severity)}
            className={`memberAiFeedRow memberAiFeedRow--${event.severity.toLowerCase()}`}
          />
        )) : (
          <MemberEmptyState icon={<InsightsRoundedIcon />} title="No live alerts" hint="Pulse will surface workspace changes here." />
        )}
        </section>
        {!reportsUnavailable && <section className="memberAiReports" ref={reportsRef}>
        <header>
          <div>
            <strong>Reports</strong>
            <span>{reports.length} saved</span>
          </div>
        </header>
        {error ? <p className="memberInlineError"><ErrorOutlineRoundedIcon fontSize="small" />{error}</p> : null}
        <div className="memberAiReportList">
          {reports.length ? reports.map((report) => (
            <div className="memberAiReportListRowShell" key={report.id}>
              <PulseReportListRow
                report={report}
                onOpen={setReaderReport}
                onPin={(entry) => void pinReport(entry.id, !entry.pinned)}
                onDelete={(entry) => void deleteReport(entry.id)}
              />
            </div>
          )) : (
            <MemberEmptyState
              icon={<AutoAwesomeRoundedIcon />}
              title="No Pulse reports yet"
              hint="Generate a Snapshot to freeze the current workspace state."
            />
          )}
        </div>
        </section>}
        <Fab
          extended
          icon={<AutoAwesomeRoundedIcon fontSize="small" />}
          label="Generate report"
          className="memberGradientFab memberAiPulseFab isVisible"
          onClick={() => setRunSheetOpen(true)}
        >
          <span>Generate report</span>
        </Fab>
      </div>
      <MemberBottomSheet
        open={Boolean(activeMetricCopy)}
        onClose={() => setActiveMetric(null)}
        title={activeMetricCopy?.label ?? 'Pulse detail'}
        testId="member-ai-pulse-metric-sheet"
      >
        {activeMetricCopy ? (
          <div className="memberAiMetricSheet">
            <strong>{activeMetricCopy.value}</strong>
            {activeMetric === 'risks' && pulse.topRisks.length ? (
              <div className="memberAiMetricRows">
                {pulse.topRisks.map((risk) => (
                  <MemberActionCard
                    key={risk.id}
                    icon={<WarningAmberRoundedIcon fontSize="inherit" />}
                    title={risk.summary}
                    sub={risk.source}
                    status="Open"
                    statusTone="warning"
                  />
                ))}
              </div>
            ) : (
              <p>Snapshot detail is summarized for this metric.</p>
            )}
            {onOpenBoardDashboard ? (
              <Button variant="primary" size="lg" onClick={onOpenBoardDashboard}>See full detail in dashboard</Button>
            ) : null}
          </div>
        ) : null}
      </MemberBottomSheet>
      <PulseReportRunSheet
        open={runSheetOpen}
        walletCredits={walletCredits}
        runningKind={runningKind}
        onRun={(kind) => void run(kind)}
        onClose={() => setRunSheetOpen(false)}
      />
      <PulseReportReader
        report={readerReport}
        onClose={() => setReaderReport(null)}
        onRetry={(report) => void run(report.kind)}
      />
    </div>
  )
}
